import { Api } from '$api';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { toastActions } from '$store/toast';
import { cloudActions, cloudSelector } from '$store/cloud/index';
import {
  AddFilesToAlbumAction,
  CloudState,
  DeleteItemsFromAlbumAction,
  LoadItemsAction,
  UploadCoverAction,
} from '$store/cloud/interface';
import { goBack } from '$navigation/helper';
import { CLOUD_ALL_FILES_ALBUM_ID } from '$shared/constants/cloud';
import { postsActions } from '$store/posts';
import { CloudMediaTabs } from '$store/models';
import { _confirm, canvasToBlob, imageLoad, releaseCanvas } from '$utils';
import { attachActions } from '$store/attach';
import { t } from '$translation';
import { profileActions } from '$store/profile';
import * as loadImage from 'blueimp-load-image';

function* loadAlbumsWorker() {
  try {
    const data = yield call(Api.get, `/albums`);

    yield put(cloudActions.setAlbums(data.albums));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadItemsWorker(action: LoadItemsAction) {
  const { albumId, isAsc, startFrom, isMore, filter } = action.payload;

  try {
    const { routeParams } = yield select(cloudSelector);
    const peerId = +routeParams.peerId || 0;

    const params: any = {
      desc: !isAsc,
      startFrom,
    };
    if (filter !== CloudMediaTabs.ALL) {
      params.only = filter;
    }

    if (peerId > 0) {
      params.peer = peerId;
    }

    const data = yield call(Api.get, `/albums/${albumId ? `${albumId}` : 'all'}`, {
      params: params,
    });

    yield put(
      cloudActions.setItems({
        albumId: albumId || CLOUD_ALL_FILES_ALBUM_ID,
        items: data.elements,
        isAsc,
        title: data.album?.title || '',
        coverUrl: data.album?.coverUrl || '',
        nextFrom: data.next,
        isMore,
      }),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield call(goBack);
  }
}

function* deleteItemsFromAlbumWorker(action: DeleteItemsFromAlbumAction) {
  const { items } = action.payload;

  try {
    yield call(Api.post, `/albums/delete_elements`, {
      items: items.map((item) => ({ id: item.id, aid: item.albumId })),
    });

    yield put(cloudActions.removeItemsFromAlbum({ items }));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* publishPostWorker() {
  yield put(cloudActions.setSelectedItems([]));
}

function* addFilesToAlbumWorker(action: AddFilesToAlbumAction) {
  const { albumId, items } = action.payload;
  const { itemsInfo, filter, selectedAlbumId }: CloudState = yield select(cloudSelector);
  const isAsc = itemsInfo[selectedAlbumId]?.isAsc;

  try {
    yield call(Api.post, `/albums/${albumId}/add`, {
      elements: items,
    });
    yield put(cloudActions.loadAlbums());
    yield put(
      cloudActions.loadItems({
        albumId: selectedAlbumId === CLOUD_ALL_FILES_ALBUM_ID ? null : selectedAlbumId,
        isAsc,
        filter,
        startFrom: 0,
      }),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* uploadCoverWorker(action: UploadCoverAction) {
  const { albumId, file } = action.payload;

  try {
    yield put(toastActions.setToastLoading());
    const img = yield call(loadImage, file, {
      maxWidth: 1920,
      maxHeight: 1920,
      meta: true,
      canvas: true,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
      orientation: true,
    });

    const blob = yield call(canvasToBlob, img.image, 0.85);

    if (blob) {
      const formData = new FormData();
      formData.append('file', blob);
      formData.append('album_id', '-6');

      const uploadPhotoData = yield call(Api.post, '/photos/upload', formData);
      const data = yield call(Api.post, `/albums/${albumId}/cover`, {
        photoId: uploadPhotoData.photo_id,
      });

      if (data.album) {
        yield put(
          cloudActions.setAlbumCoverUrl({
            albumId: data.album.id + '',
            coverUrl: data.album.coverUrl,
          }),
        );
        yield put(toastActions.setToastSuccess());
      } else {
        yield put(toastActions.setToastFail());
      }
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* cloudSaga() {
  yield all([takeEvery(cloudActions.loadAlbums, loadAlbumsWorker)]);
  yield all([takeEvery(cloudActions.loadItems, loadItemsWorker)]);
  yield all([takeEvery(cloudActions.deleteItemsFromAlbum, deleteItemsFromAlbumWorker)]);
  yield all([takeEvery(postsActions.publish, publishPostWorker)]);
  yield all([takeEvery(cloudActions.addFilesToAlbum, addFilesToAlbumWorker)]);
  yield all([takeLatest(cloudActions.uploadCover, uploadCoverWorker)]);
}
